/**
 * ChallengeJoin — secure enrollment page for the 4-Week Pro Challenge.
 * Route: /challenge/join/:token (token = signed invite JWT, no DB ids exposed).
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Trophy, CircleNotch, CheckCircle } from "@phosphor-icons/react";

const fmt = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

export default function ChallengeJoin() {
  const { token } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/pro-challenge/invite/${token}`)
      .then(({ data }) => setInfo(data))
      .catch((e) => setError(e?.response?.data?.detail || "This invitation link isn't valid."));
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    try {
      const { data } = await api.post(`/pro-challenge/invite/${token}/accept`);
      setDone(true);
      toast.success(data.already_enrolled ? "You're already enrolled!" : "Challenge accepted — good luck!");
      setTimeout(() => navigate("/dashboard"), 2200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't enroll — please try again");
    } finally {
      setAccepting(false);
    }
  };

  if (error) {
    return (
      <div className="mx-auto max-w-lg px-4 py-20 text-center" data-testid="challenge-join-error">
        <p className="font-semibold text-lg">Hmm, that link didn't work.</p>
        <p className="mt-2 text-muted-foreground text-sm">{error}</p>
      </div>
    );
  }
  if (!info || authLoading) {
    return <div className="grid place-items-center py-32" data-testid="challenge-join-loading"><CircleNotch className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="mx-auto max-w-xl px-4 py-10" data-testid="challenge-join-page">
      <div className="rounded-3xl border bg-card p-7 sm:p-9">
        <div className="h-12 w-12 rounded-2xl bg-primary/10 grid place-items-center">
          <Trophy weight="duotone" className="h-6 w-6 text-primary" />
        </div>
        <h1 className="mt-4 font-display font-bold text-2xl sm:text-3xl tracking-tight">The 4-Week Pro Challenge</h1>
        <p className="mt-1.5 text-muted-foreground text-sm">
          Enrolling: <span className="font-semibold text-foreground" data-testid="challenge-join-truck-name">{info.truck_name}</span>
        </p>

        <div className="mt-5 rounded-xl bg-muted/50 p-4 text-sm leading-relaxed" data-testid="challenge-join-terms">
          {info.terms}
        </div>

        <div className="mt-5">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Your four challenge weeks</div>
          <div className="mt-2 grid gap-1.5" data-testid="challenge-join-weeks">
            {info.weeks_preview.map((w) => (
              <div key={w.index} className="flex items-center gap-3 rounded-lg border px-3.5 py-2 text-sm">
                <span className="font-bold text-primary w-16">Week {w.index}</span>
                <span>{fmt(w.start)} → {fmt(w.end)}</span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">Weeks run Sunday–Saturday (Eastern). Week 1 is this current week.</p>
        </div>

        {done ? (
          <div className="mt-6 rounded-xl bg-green-50 border border-green-200 text-green-800 px-4 py-3 font-medium flex items-center gap-2" data-testid="challenge-join-success">
            <CheckCircle weight="fill" className="h-5 w-5" /> You're enrolled! Taking you to your dashboard…
          </div>
        ) : info.already_enrolled ? (
          <div className="mt-6">
            <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 text-sm font-medium" data-testid="challenge-join-already">
              This truck is already enrolled in the challenge.
            </div>
            <Button asChild className="mt-3 rounded-full"><Link to="/dashboard">View my progress</Link></Button>
          </div>
        ) : !user ? (
          <div className="mt-6">
            <p className="text-sm text-muted-foreground">Log in to your owner account to accept the challenge.</p>
            <Button asChild className="mt-3 rounded-full h-11 px-7 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="challenge-join-login-btn">
              <Link to={`/login?next=${encodeURIComponent(`/challenge/join/${token}`)}`}>Log in to continue</Link>
            </Button>
          </div>
        ) : (
          <Button
            onClick={accept}
            disabled={accepting || !info.campaign_enabled}
            className="mt-6 w-full rounded-full h-12 text-base bg-primary text-primary-foreground hover:bg-primary/90 gap-2"
            data-testid="challenge-join-accept-btn"
          >
            {accepting && <CircleNotch className="h-4 w-4 animate-spin" />}
            {info.campaign_enabled ? "Accept Challenge" : "Challenge opens soon"}
          </Button>
        )}
        <p className="mt-4 text-[11px] text-muted-foreground text-center">
          No credit card required · no automatic renewal · no charge at the end.
        </p>
      </div>
    </div>
  );
}
